import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
  CdkDropList,
  CdkDrag,
} from '@angular/cdk/drag-drop';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  PivotTableField,
  PivotTableHeader,
  PivotTableValue,
} from '../../raw-pivot-table/raw-pivot-table.component';
import { PillComponent } from '../../pill/pill.component';

@Component({
  selector: 'ag-field-drawer',
  templateUrl: './field-drawer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CdkDropList, CdkDrag, PillComponent],
})
export class FieldDrawerComponent {
  private overlay = inject(Overlay);
  private _viewContainerRef = inject(ViewContainerRef);

  @ViewChild('valuesPortalContent')
  valuesPortalContent: TemplateRef<unknown>;
  valuesOverlayRef: OverlayRef;

  @ViewChild('headersPortalContent')
  headersPortalContent: TemplateRef<unknown>;

  constructor() {}

  @Input()
  possibleFields: PivotTableField[] = [];
  @Input()
  cols: PivotTableHeader[] = [];
  @Input()
  rows: PivotTableHeader[] = [];
  @Input()
  values: PivotTableValue[] = [];

  @Output()
  colsChange = new EventEmitter<PivotTableHeader[]>();
  @Output()
  rowsChange = new EventEmitter<PivotTableHeader[]>();
  @Output()
  valuesChange = new EventEmitter<PivotTableValue[]>();
  @Output()
  configChange = new EventEmitter();

  private set _cols(value: PivotTableHeader[]) {
    this.cols = value;
    this.colsChange.emit(value);
    this.configChange.emit();
  }

  private set _rows(value: PivotTableHeader[]) {
    this.rows = value;
    this.rowsChange.emit(value);
    this.configChange.emit();
  }

  private set _values(value: PivotTableValue[]) {
    this.values = value;
    this.valuesChange.emit(value);
    this.configChange.emit();
  }

  handleRemoveColClick(header: PivotTableHeader) {
    this.handleRemoveClick(header, this.cols, 'col');
  }

  handleRemoveRowClick(header: PivotTableHeader) {
    this.handleRemoveClick(header, this.rows, 'row');
  }

  handleRemoveValueClick(header: PivotTableHeader) {
    this.handleRemoveClick(header, this.values, 'value');
  }

  handleRemoveClick(
    header: any,
    dataSource: any[],
    type: 'row' | 'col' | 'value',
  ) {
    const rowIndex = dataSource.indexOf(header);

    const newData = [...dataSource];
    newData.splice(rowIndex, 1);

    if (type == 'row') this._rows = newData;
    if (type == 'col') this._cols = newData;
    if (type == 'value') this._values = newData;
  }

  drop(event: CdkDragDrop<PivotTableHeader[]>) {
    // default to count aggregation, as all types support this
    if (event.container.id === 'valuesList') {
      const item = event.previousContainer.data[
        event.previousIndex
      ] as unknown as PivotTableValue;
      if (!item.operator) item.operator = 'count';
    }

    // dragging item inside same container, just re-order it
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
    // dragging an item from the available list shouldnt remove it
    else if (event.previousContainer.id == 'possibleFieldsList') {
      const item = event.previousContainer.data[event.previousIndex];

      // just insert item into new section
      event.container.data.splice(event.currentIndex, 0, item);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }

    // if we dragged an item into or out of an area, we need to emit the appropriate change event
    if (
      !(
        event.container.id == 'possibleFieldsList' &&
        event.container.id == event.previousContainer.id
      )
    ) {
      this.handleActiveFieldsChanged(event);
    }
  }

  handleActiveFieldsChanged(event: CdkDragDrop<PivotTableHeader[]>) {
    if ([event.container.id, event.previousContainer.id].includes('rowsList'))
      this._rows = [...this.rows];

    if ([event.container.id, event.previousContainer.id].includes('colsList'))
      this._cols = [...this.cols];

    if ([event.container.id, event.previousContainer.id].includes('valuesList'))
      this._values = [...this.values];
  }

  handleTotalChanged($event, value: PivotTableHeader, type: 'row' | 'col') {
    value.total = $event.target.checked;

    if (type == 'row') this._rows = [...this.rows];
    if (type == 'col') this._cols = [...this.cols];
  }

  handleAggregationChanged(value: PivotTableValue, newOperator) {
    value.operator = newOperator;

    this._values = [...this.values];
  }

  openHeadersOverlay(origin, value, type: 'row' | 'col') {
    if (this.valuesOverlayRef) this.valuesOverlayRef.dispose();

    this.valuesOverlayRef = this.buildOverlay(
      origin,
      value,
      this.headersPortalContent,
      type,
    );
  }

  openValuesOverlay(origin, value) {
    if (this.valuesOverlayRef) this.valuesOverlayRef.dispose();

    this.valuesOverlayRef = this.buildOverlay(
      origin,
      value,
      this.valuesPortalContent,
      'value',
    );
  }

  buildOverlay(origin, value, template, type: 'row' | 'col' | 'value') {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions([
        {
          // here, top-left of the overlay is connected to bottom-left of the origin;
          // of course, you can change this object or generate it dynamically;
          // moreover, you can specify multiple objects in this array for CDK to find the most suitable option
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        } as ConnectedPosition,
      ])
      .withPush(true);

    // We create the overlay
    const overlayRef = this.overlay.create({
      positionStrategy: positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    //Then we create a portal to render a component
    const componentPortal = new TemplatePortal(
      template,
      this._viewContainerRef,
      { $implicit: value, type: type },
    );

    //We render the portal in the overlay
    overlayRef.attach(componentPortal);

    overlayRef.backdropClick().subscribe((s) => {
      this.valuesOverlayRef.dispose();
    });

    return overlayRef;
  }
}
